import { useMemo, useState } from "react";
import {
	useReactTable,
	getCoreRowModel,
	getSortedRowModel,
	getFilteredRowModel,
	getPaginationRowModel, // Add for pagination
	flexRender,
} from "@tanstack/react-table";
import { FormattedMessage } from "react-intl"; // Assuming you're using react-intl

function WetLikeTable({ sampleExhibitsFromGCCase, isLoggedIn, listLinks, downloadAllDocs, downloadUrls, dateFormatter, downloadLink, messages, buttonRef, setIsAllSelected }) {
	const [filterValue, setFilterValue] = useState(""); // State for filter input
	const [pagination, setPagination] = useState({
		pageIndex: 0,
		pageSize: -1, // Default to show all rows
	});
	const [sorting, setSorting] = useState([{ id: "downloadLink", desc: false }]); // Persist sorting state
	
	// Define columns to match your WET table structure
	const columns = useMemo(
		() => [
			{
				id: "ace_placedonexhibitlistingText",
				header: () => <FormattedMessage id="ace.exhibit.table.header.1" />,
				accessorKey: "ace_placedonexhibitlistingText",
				cell: ({ getValue }) => (
					<div style={{ whiteSpace: "nowrap" }}>{dateFormatter(getValue())}</div>
				),
				filterFn: (row, columnId, filterValue) => {
					const value = row.getValue(columnId) || ""; // Handle null/undefined
					return value.toLowerCase().includes(filterValue.toLowerCase());
				},
				enableSorting: true
			},
			{
				header: () => <><FormattedMessage id="ace.exhibit.table.header.2" /><br /></>,
				accessorFn: (row) => {
					// Extract a sortable value from the row
					// Replace 'ace_url' with the actual field downloadLink uses, if available
					return row.ace_name || String(downloadLink(row)); // Fallback to stringified output
				},
				id: "downloadLink", // Use id instead of accessorKey for custom accessors
				cell: ({ row }) => (
				  <div style={{ whiteSpace: "nowrap" }}>{downloadLink(row.original, row.index)}</div>
				),
				enableSorting: true,
			},
			{
				id: "ace_classText",
				header: () => (
				<>
					<FormattedMessage id="ace.exhibit.table.header.3" />
					<sup id="fn1-rf">
						<a className="fn-lnk" href="#fn1">
							<span className="wb-inv">Footnote </span>1
						</a>
					</sup>
				</>
				),
				accessorKey: "ace_classText",
				filterFn: (row, columnId, filterValue) => {
					const value = row.getValue(columnId) || ""; // Handle null/undefined
					return value.toLowerCase().includes(filterValue.toLowerCase());
				},
				enableSorting: false, // Matches "orderable": false for column 2
			},
			{
				id: "ace_datereceivedText",
				header: () => <FormattedMessage id="ace.exhibit.table.header.4" />,
				accessorKey: "ace_datereceivedText",
				cell: ({ getValue }) => (
					<div style={{ whiteSpace: "nowrap" }}>{dateFormatter(getValue())}</div>
				),
				// was not in the non-filtered list
				filterFn: (row, columnId, filterValue) => {
					const rawValue = row.getValue(columnId);
					const formattedValue = rawValue ? dateFormatter(rawValue) : "";
					return String(formattedValue || "").toLowerCase().includes(filterValue.toLowerCase());
				},
				enableSorting: true
			},
			{
				id: "ace_drdpresent",
				header: () => (
					<FormattedMessage
						id="ace.exhibit.table.header.5"
						values={{
							abbr: (chunks) => (
								<><abbr title={messages["ace.exhibit.DRD.abbr"]}>{chunks}</abbr><br /></>
							),
						}}
					/>
				),
				accessorKey: "ace_drdpresent",
				cell: ({ getValue }) => (
					<FormattedMessage id={getValue() ? "label.yes" : "label.no"} />
				),
				filterFn: (row, columnId, filterValue) => {
					const value = row.getValue(columnId);
					const displayValue = value ? messages["label.yes"] : messages["label.no"];
					return displayValue.toLowerCase().includes(filterValue.toLowerCase());
				},
				enableSorting: true
			},
			{
				id: "ace_officialdateofsubmissionreceiptText",
				header: () => (
					<FormattedMessage
						id="ace.exhibit.table.header.6"
						values={{
							abbr: (chunks) => (
								<abbr title={messages["ace.exhibit.footnote.SIMA.abbr"]}>
									{chunks}
								</abbr>
							),
						}}
					/>
				),
				accessorKey: "ace_officialdateofsubmissionreceiptText",
				cell: ({ getValue }) => (
					<div style={{ whiteSpace: "nowrap" }}>{dateFormatter(getValue())}</div>
				),
				filterFn: (row, columnId, filterValue) => {
					const value = row.getValue(columnId) || ""; // Handle null/undefined
					return value.toLowerCase().includes(filterValue.toLowerCase());
				},
				enableSorting: true
			},
			{
				id: "ace_authorText",
				header: () => (
					<>
						<FormattedMessage id="ace.exhibit.table.header.7" />
						<sup id="fn2-rf">
							<a className="fn-lnk" href="#fn2">
								<span className="wb-inv">Footnote </span>2
							</a>
						</sup>
					</>
				),
				accessorKey: "ace_authorText",
				enableSorting: false, // Matches "orderable": false for column 6
			},
			{
				id: "ace_description",
				header: () => <FormattedMessage id="ace.exhibit.table.header.8" />,
				accessorKey: "ace_description",
				enableSorting: false, // Matches "orderable": false for column 7
			},
			{
				id: "ace_counselText",
				header: () => <FormattedMessage id="ace.exhibit.table.header.9" />,
				accessorKey: "ace_counselText",
				filterFn: (row, columnId, filterValue) => {
					const value = row.getValue(columnId) || ""; // Handle null/undefined
					return value.toLowerCase().includes(filterValue.toLowerCase());
				},
				enableSorting: true
			},
		],
		[messages, dateFormatter, downloadLink] // Dependencies updated
	);

	// Initialize TanStack Table
	const table = useReactTable({
		data: sampleExhibitsFromGCCase,
		columns,
		getCoreRowModel: getCoreRowModel(),
		getSortedRowModel: getSortedRowModel(),
		getFilteredRowModel: getFilteredRowModel(),
		getPaginationRowModel: getPaginationRowModel(), // Add pagination
		state: {
			globalFilter: filterValue, // Connect filter state
			pagination,
			sorting // Controlled sorting state
		},
		onGlobalFilterChange: setFilterValue, // Update filter state
		onPaginationChange: setPagination, // Handle pagination updates
		onSortingChange: setSorting, // Handle sorting changes
		globalFilterFn: (row, columnIds, filterValue) => {
			return columns.some((column) => {
				const columnId = column.id;
				if (!columnId) {
					console.warn("Skipping column with undefined id:", column);
					return false;
				}
				const filterFn = column.filterFn;
				if (filterFn && typeof filterFn === "function") {
					try {
					  return filterFn(row, columnId, filterValue);
					} catch (error) {
					  console.error(`Filter error for column ${columnId}:`, error);
					  return false;
					}
				}
				const value = row.getValue(columnId);
				return String(value || "").toLowerCase().includes(filterValue.toLowerCase());
			});
		},
		initialState: {
			sorting: [{ id: "downloadLink", desc: false }],
			pagination: { pageSize: -1 },
		},
	});
  
	// Workaround: Manually construct rows from rowsById if rows is incomplete
	const allRows = (() => {
		if (
			table.getState().pagination.pageSize === -1 ||
			table.getState().pagination.pageSize === table.getFilteredRowModel().rows.length
		) {
			const rows = Object.values(table.getRowModel().rowsById);
			const sorting = table.getState().sorting;
			if (sorting.length > 0) {
				const { id, desc } = sorting[0];
				return rows.sort((a, b) => {
				const aValue = a.getValue(id);
				const bValue = b.getValue(id);
				if (aValue == null && bValue == null) return 0;
				if (aValue == null) return desc ? -1 : 1;
				if (bValue == null) return desc ? 1 : -1;
				if (aValue < bValue) return desc ? 1 : -1;
				if (aValue > bValue) return desc ? -1 : 1;
				return 0;
				});
			}
			return rows;
		}
		return table.getRowModel().rows;
	})();
	
	const pageSizeOptions = [5, 50, 100, -1]; // Matches WET’s options: 5, 50, 100, All

	// Calculate page numbers to display (e.g., show up to 5 pages around current)
	const getPageNumbers = () => {
		const pageCount = table.getPageCount();
		const currentPage = table.getState().pagination.pageIndex;
		const maxPagesToShow = 5; // Show up to 5 page numbers
		const half = Math.floor(maxPagesToShow / 2);
		let startPage = Math.max(0, currentPage - half);
		let endPage = Math.min(pageCount, startPage + maxPagesToShow);
	
		if (endPage - startPage < maxPagesToShow) {
			startPage = Math.max(0, endPage - maxPagesToShow);
		}
	
		return Array.from({ length: endPage - startPage }, (_, i) => startPage + i);
	};

	return (
		<div id="wb-auto-4_wrapper" className="dataTables_wrapper no-footer">
			<div className="top">
				{/* Filter Input */}
				<div id="wb-auto-4_filter" className="dataTables_filter">
					<label 
						// htmlFor="table-filter"
					>
						<FormattedMessage id="ace.exhibit.table.filter.label" />
						<input
							// id="table-filter"
							type="search"
							value={filterValue}
							onChange={(e) => setFilterValue(e.target.value)}
							// className="form-control"
							placeholder=""
							// aria-label="Filter table"
							// aria-controls="wb-auto-4" // accessibility said it was invalid
						/>
					</label>
				</div>

				{/* DataTables Info */}
				<div id="wb-auto-4_info" className="dataTables_info" role="status" aria-live="polite">
					<FormattedMessage
						id="ace.exhibit.table.info"
						values={{
							start: allRows.length
								? table.getState().pagination.pageIndex * table.getState().pagination.pageSize + 1
								: 0,
							end:
								table.getState().pagination.pageSize === -1 ||
								table.getState().pagination.pageSize === table.getFilteredRowModel().rows.length
								? table.getFilteredRowModel().rows.length
								: Math.min(
									(table.getState().pagination.pageIndex + 1) * table.getState().pagination.pageSize,
									table.getFilteredRowModel().rows.length
								),
							total: table.getFilteredRowModel().rows.length,
						}}
					/>
				</div>

				{/* DataTables Length */}
				<div id="wb-auto-4_length" className="dataTables_length">
					<label>
						<FormattedMessage id="ace.exhibit.table.length.label" />
						<select
							name="wb-auto-4_length"
							// aria-controls="wb-auto-4" // accessibility said it was invalid
							value={table.getState().pagination.pageSize}
							onChange={(e) => {
								const newSize = Number(e.target.value);
								setPagination((prev) => ({
									...prev,
									pageSize: newSize === -1 ? -1 : newSize,
									pageIndex: 0,
								}));
							}}
						>
							{pageSizeOptions.map((size) => (
								<option key={size} value={size}>
									{size === -1 ? messages["ace.exhibit.table.length.all"] : size}
								</option>
							))}
						</select>
						<FormattedMessage id="ace.exhibit.table.length.entries" />
					</label>
				</div>
			</div>
			
			<table className="table table-striped table-hover dataTable">
				<thead>
					{table.getHeaderGroups().map((headerGroup) => (
						<tr key={headerGroup.id}>
							{headerGroup.headers.map((header) => (
								<th
									key={header.id}
									scope="col"
									className={
										header.column.getCanSort()
										? header.column.getIsSorted()
											? `sorting_${header.column.getIsSorted() === "asc" ? "asc" : "desc"}`
											: "sorting"
										: ""
									}
									onClick={header.column.getCanSort() ? header.column.getToggleSortingHandler() : undefined}
									style={{ cursor: header.column.getCanSort() ? "pointer" : "default" }}
									tabIndex={header.column.getCanSort() ? 0 : undefined}
									// aria-sort={header.column.getIsSorted() ? header.column.getIsSorted() : "none"} // accessibility said it was invalid
									aria-label={`${header.column.columnDef.header} activate for ${
										header.column.getIsSorted() === "asc" ? "descending" : "ascending"
									} sort`}
								>
									{flexRender(header.column.columnDef.header, header.getContext())}
									{header.column.getCanSort() && (
										<span className="sorting-cnt">
											<span className="sorting-icons"></span>
										</span>
									)}
								</th>
							))}
						</tr>
					))}
				</thead>
				<tbody>
					{allRows.map((row) => (
						<tr
							key={row.id}
							className={
								isLoggedIn && row.original.ace_FileSize !== 0 ? "selectable-row" : ""
							}
							onClick={() => listLinks(row.original, row.index)}
						>
							{row.getVisibleCells().map((cell) => (
								<td key={cell.id}>
									{flexRender(cell.column.columnDef.cell, cell.getContext())}
								</td>
							))}
						</tr>
					))}
				</tbody>
			</table>

			{/* Pagination Controls */}
			{table.getState().pagination.pageSize !== -1 &&
				table.getState().pagination.pageSize !== table.getFilteredRowModel().rows.length &&
				table.getPageCount() > 1 && (
				<div className="bottom">
					<div className="dataTables_paginate paging_simple_numbers" id="wb-auto-4_paginate">
						<a
							className="paginate_button previous"
							// aria-controls="wb-auto-4" // accessibility said it was invalid
							tabIndex={0}
							onClick={() => table.previousPage()}
							disabled={!table.getCanPreviousPage()}
						>
							<FormattedMessage id="ace.exhibit.table.paginate.previous" />
						</a>
						<span>
							{getPageNumbers().map((pageNum) => (
								<a
									key={pageNum}
									className={`paginate_button ${pageNum === table.getState().pagination.pageIndex ? "current" : ""}`}
									// aria-controls="wb-auto-4" // accessibility said it was invalid
									tabIndex={0}
									onClick={() => table.setPageIndex(pageNum)}
								>
									{pageNum + 1}
								</a>
							))}
						</span>
						<a
							className="paginate_button next"
							// aria-controls="wb-auto-4" // accessibility said it was invalid
							tabIndex={0}
							onClick={() => table.nextPage()}
							disabled={!table.getCanNextPage()}
						>
							<FormattedMessage id="ace.exhibit.table.paginate.next" />
						</a>
					</div>
				</div>
			)}

			<button
				ref={buttonRef}
				id="download-button"
				className="download-button"
				onClick={() => {downloadAllDocs(); setIsAllSelected(false);}}
				style={{ visibility: "hidden", display: "none" }}
			/>
			{Object.keys(downloadUrls).map((downloadUrlKey) => 
				<a key={downloadUrlKey+"-url"} id={downloadUrlKey+"-url"} href={downloadUrls[downloadUrlKey].url} style={{display:"none", visibility:"hidden"}}>{downloadUrls[downloadUrlKey].url}</a>
			)}
		</div>
	);
};

export default WetLikeTable;